/**
 * NOTE: Use Box with Flexbox for layout, NOT Grid
 * Grid creates ugly layouts - prefer Box with display: flex
 */
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Paper,
    IconButton,
    CircularProgress
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle,
    Psychology,
    Download as DownloadIcon,
    Assessment as AssessmentIcon,
    InsertChart as ChartIcon,
    Image as ImageIcon,
    TableChart as TableIcon
} from '@mui/icons-material';
import axiosDirectApi from 'utils/axiosDirectApi';

const ModelDetailsDialog = ({ open, onClose, model }) => {
    const [artifacts, setArtifacts] = useState([]);
    const [loadingArtifacts, setLoadingArtifacts] = useState(false);
    const [modelWeights, setModelWeights] = useState(null);

    // Load artifacts when dialog opens
    useEffect(() => {
        const loadArtifacts = async () => {
            if (!open || !model) {
                return;
            }

            setLoadingArtifacts(true);
            try {
                // Artifacts from training job
                const response = await axiosDirectApi.get(`/cv/training/jobs/${model.training_job_id}/artifacts`);
                setArtifacts(response.data.artifacts || []);
            } catch (error) {
                console.error('Failed to load artifacts:', error);
                setArtifacts([]);
            } finally {
                setLoadingArtifacts(false);
            }
        };

        loadArtifacts();
    }, [open, model]);

    const handleDownloadArtifact = async (filename) => {
        try {
            const response = await axiosDirectApi.get(`/cv/training/jobs/${model.training_job_id}/artifacts/${filename}`);
            // Open presigned URL in new tab
            window.open(response.data.url, '_blank');
        } catch (error) {
            console.error('Failed to download artifact:', error);
        }
    };

    const handleDownloadModel = async () => {
        try {
            const response = await axiosDirectApi.get(`/cv/models/${model.id}/download`);
            window.open(response.data.url, '_blank');
        } catch (error) {
            console.error('Failed to download model:', error);
        }
    };

    const getArtifactIcon = (type) => {
        switch (type) {
            case 'metrics':
                return <TableIcon color="primary" />;
            case 'plot':
            case 'curve':
            case 'confusion_matrix':
                return <ChartIcon color="success" />;
            case 'validation':
            case 'labels':
                return <ImageIcon color="warning" />;
            default:
                return <AssessmentIcon />;
        }
    };

    // Early return after all hooks
    if (!model) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '80vh',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, #43cea2 0%, #185a9d 100%)',
                    color: 'white',
                    py: 2.5
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={2}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                bgcolor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Psychology sx={{ color: 'white', fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {model.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Chip
                                    label={`v${model.version}`}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.25)',
                                        color: 'white',
                                        fontWeight: 600
                                    }}
                                />
                                <Chip
                                    icon={<CheckCircle sx={{ color: 'white !important' }} />}
                                    label={model.is_deployed ? 'DEPLOYED' : 'READY'}
                                    size="small"
                                    sx={{
                                        bgcolor: model.is_deployed ? 'rgba(255,193,7,0.9)' : 'rgba(76,175,80,0.9)',
                                        color: 'white',
                                        fontWeight: 600,
                                        '& .MuiChip-icon': { color: 'white' }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ bgcolor: '#f8f9fa', p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Basic Info Cards */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    height: '100%',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: 'white'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    MODEL TYPE
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 1, fontWeight: 700, color: 'success.main' }}>
                                    {model.model_type.toUpperCase()}
                                </Typography>
                                <Chip label={model.framework} size="small" sx={{ mt: 1, fontWeight: 500 }} variant="outlined" />
                            </Paper>
                        </Box>

                        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    height: '100%',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: 'white'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    CREATED
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
                                    {new Date(model.created_at).toLocaleDateString('vi-VN')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {new Date(model.created_at).toLocaleTimeString('vi-VN')}
                                </Typography>
                            </Paper>
                        </Box>

                        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    height: '100%',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: 'white'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    FILE SIZE
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 1, fontWeight: 700, color: 'primary.main' }}>
                                    {model.size_mb ? `${model.size_mb.toFixed(1)} MB` : 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Model weights
                                </Typography>
                            </Paper>
                        </Box>
                    </Box>

                    {/* Additional Info Row */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {/* Dataset Info */}
                        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    height: '100%',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: 'white'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    DATASET ID
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                    {model.dataset_id?.substring(0, 16)}...
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Training dataset
                                </Typography>
                            </Paper>
                        </Box>

                        {/* Input Size */}
                        {model.input_size && model.input_size.length > 0 && (
                            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.5,
                                        height: '100%',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: 'white'
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        INPUT SIZE
                                    </Typography>
                                    <Typography variant="h6" sx={{ mt: 1, fontWeight: 700, color: 'info.main' }}>
                                        {model.input_size[0]} × {model.input_size[1]}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Width × Height
                                    </Typography>
                                </Paper>
                            </Box>
                        )}

                        {/* Parameters */}
                        {model.parameters && (
                            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.5,
                                        height: '100%',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: 'white'
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        PARAMETERS
                                    </Typography>
                                    <Typography variant="h6" sx={{ mt: 1, fontWeight: 700, color: 'secondary.main' }}>
                                        {(model.parameters / 1000000).toFixed(1)}M
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Model parameters
                                    </Typography>
                                </Paper>
                            </Box>
                        )}
                    </Box>

                    {/* Description */}
                    {model.description && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'white'
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'text.primary', mb: 1 }}>
                                Description
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                {model.description}
                            </Typography>
                        </Paper>
                    )}

                    {/* Deployment Info */}
                    {model.is_deployed && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '2px solid',
                                borderColor: 'warning.main',
                                borderRadius: 2,
                                bgcolor: '#fffbf0'
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'warning.main', mb: 2 }}>
                                Deployment Information
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {model.deployment_type && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                            Type:
                                        </Typography>
                                        <Chip label={model.deployment_type.toUpperCase()} size="small" color="warning" />
                                    </Box>
                                )}
                                {model.deployment_url && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                            URL:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                                            {model.deployment_url}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    )}

                    {/* Performance Metrics */}
                    {model.metrics && Object.keys(model.metrics).length > 0 && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'white'
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'text.primary', mb: 2 }}>
                                Performance Metrics
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {Object.entries(model.metrics).map(([key, value]) => (
                                    <Box
                                        key={key}
                                        sx={{
                                            flex: '1 1 180px',
                                            minWidth: 0,
                                            p: 2.5,
                                            borderRadius: 2,
                                            bgcolor: 'linear-gradient(135deg, rgba(67, 206, 162, 0.05) 0%, rgba(24, 90, 157, 0.05) 100%)',
                                            border: '2px solid',
                                            borderColor: 'success.light',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight={600}
                                            sx={{ textTransform: 'uppercase' }}
                                        >
                                            {key.replace(/_/g, ' ')}
                                        </Typography>
                                        <Typography variant="h5" fontWeight={700} sx={{ mt: 1, color: 'success.main' }}>
                                            {typeof value === 'number' ? value.toFixed(3) : value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    )}

                    {/* Classes */}
                    {model.classes && model.classes.length > 0 && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'white'
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'text.primary', mb: 2 }}>
                                Classes ({model.classes.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {model.classes.map((className, index) => (
                                    <Chip
                                        key={index}
                                        label={className}
                                        size="medium"
                                        sx={{
                                            fontWeight: 500,
                                            bgcolor: 'grey.100',
                                            '&:hover': {
                                                bgcolor: 'grey.200'
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                        </Paper>
                    )}

                    {/* Model Weights Download */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'white'
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'text.primary', mb: 2 }}>
                            Model Weights
                        </Typography>
                        <Box
                            sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                bgcolor: '#fafbfc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    borderColor: 'success.main',
                                    bgcolor: 'white',
                                    boxShadow: '0 4px 12px rgba(67, 206, 162, 0.15)',
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Psychology color="success" fontSize="large" />
                                <Box>
                                    <Typography variant="body1" fontWeight={600}>
                                        {model.model_file || 'best.pt'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Trained model weights
                                    </Typography>
                                </Box>
                            </Box>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadModel}
                                sx={{
                                    fontWeight: 600
                                }}
                            >
                                Download
                            </Button>
                        </Box>
                    </Paper>

                    {/* Training Artifacts */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'white'
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'text.primary', mb: 2 }}>
                            Training Artifacts
                        </Typography>
                        {loadingArtifacts ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                                <CircularProgress size={40} thickness={4} />
                            </Box>
                        ) : artifacts.length === 0 ? (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    py: 4,
                                    bgcolor: '#f8f9fa',
                                    borderRadius: 1.5,
                                    border: '1px dashed',
                                    borderColor: 'divider'
                                }}
                            >
                                <AssessmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                    No artifacts available
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {artifacts.map((artifact) => (
                                    <Box
                                        key={artifact.filename}
                                        sx={{
                                            flex: '1 1 280px',
                                            minWidth: 0,
                                            p: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1.5,
                                            bgcolor: '#fafbfc',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                borderColor: 'success.main',
                                                bgcolor: 'white',
                                                boxShadow: '0 4px 12px rgba(67, 206, 162, 0.15)',
                                                transform: 'translateY(-2px)'
                                            }
                                        }}
                                    >
                                        <Box display="flex" alignItems="flex-start" gap={1.5}>
                                            <Box sx={{ mt: 0.5 }}>{getArtifactIcon(artifact.type)}</Box>
                                            <Box flex={1} sx={{ minWidth: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {artifact.filename}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {artifact.type} • {(artifact.size / 1024).toFixed(1)} KB
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                onClick={() => handleDownloadArtifact(artifact.filename)}
                                                size="small"
                                                sx={{
                                                    color: 'success.main',
                                                    '&:hover': {
                                                        bgcolor: 'success.lighter'
                                                    }
                                                }}
                                            >
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Paper>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa' }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        px: 4,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 600,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #43cea2 0%, #185a9d 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #3ab892 0%, #15507e 100%)',
                            boxShadow: '0 4px 12px rgba(67, 206, 162, 0.4)'
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModelDetailsDialog;
